<template>
  <div class="flex items-center justify-between rounded-md bg-white p-4 w-full">
    <div
      :class="{
        'hidden': !canShowSideSeparator,
        'flex flex-1 justify-between ': canShowSideSeparator,
      }"
      class="md:hidden"
    >
      <a
        href="#"
        :class="{ 'cursor-not-allowed opacity-70': activePage === itemLimit }"
        class="relative inline-flex items-center rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
        @click="activePage = activePage - itemLimit"
      >
        Previous
      </a>
      <a
        href="#"
        :class="{
          'cursor-not-allowed opacity-70':
            activePage === pageLinks[pageLinks.length - 1],
        }"
        class="relative ml-3 inline-flex items-center rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
        @click="activePage = activePage + itemLimit"
      >
        Next
      </a>
    </div>
    <div
      v-if="showPermissionsPagination"
      class="w-full flex justify-between "
    >
      <a
        href="#"
        :class="{ 'cursor-not-allowed opacity-70': activePage === itemLimit }"
        class="relative inline-flex items-center rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
        @click="prevPage"
      >
        <ArrowWideLeft custom-style="text-black w-4 h-4 mr-1 " />
        Previous
      </a>
      <div>
        <a
          v-for="(page, index) in pageLinks"
          :key="`left-${index}`"
          href="#"
          :class="{
            'z-10 border-teal-500 bg-teal-50 text-teal-600':
              isActivePage(page),
            'border-gray-300 bg-white text-gray-500 hover:bg-gray-50':
              !isActivePage(page),
            'hidden':
              (canShowSideSeparator && page > lhsStartPage + 2 * itemLimit)
              || page < lhsStartPage,
          }"
          class="relative inline-flex items-center border px-4 py-2 text-sm font-medium"
          @click="changePage(page)"
        >
          {{ page / itemLimit }}
        </a>
      </div>
      <a
        href="#"
        :class="{
          'cursor-not-allowed opacity-70': activePage === pageLinks[pageLinks.length - 1],
        }"
        class="relative ml-3 inline-flex items-center rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
        @click="nextPage"
      >
        Next
        <ArrowWideLeft custom-style="w-4 h-4 rotate-180 text-black ml-1" />
      </a>
    </div>
    <div
      v-if="!showPermissionsPagination"
      :class="{
        'hidden md:flex': canShowSideSeparator,
        'flex': !canShowSideSeparator,
      }"
      class="flex-nowrap items-center justify-between gap-x-3"
    >
      <div>
        <p class="whitespace-nowrap text-sm text-gray-700">
          Showing
          <span class="font-medium">{{ showing }}</span>
          to
          <span class="font-medium">{{ itemTo }}</span>
          of
          <span class="font-medium">{{ totalItems }}</span>
          results
        </p>
      </div>
      <div>
        <nav
          class="relative z-0 inline-flex -space-x-px rounded-md shadow-sm"
          aria-label="Pagination"
        >
          <template v-if="canShowSideSeparator || lhsStartPage !== itemLimit">
            <a
              href="#"
              :class="{
                'cursor-not-allowed opacity-70': activePage === itemLimit,
              }"
              class="relative inline-flex items-center rounded-l-md border border-gray-300 bg-white px-2 py-2 text-sm font-medium text-gray-500 hover:bg-gray-50"
              @click="shiftLeft"
            >
              <span class="sr-only">Previous</span>
              <ChevronLeftSmall aria-hidden="true" />
            </a>
          </template>
          <a
            v-for="(page, index) in pageLinks"
            :key="`left-${index}`"
            href="#"
            :class="{
              'z-10 border-teal-500 bg-teal-50 text-teal-600':
                isActivePage(page),
              'border-gray-300 bg-white text-gray-500 hover:bg-gray-50':
                !isActivePage(page),
              'hidden':
                (canShowSideSeparator && page > lhsStartPage + 2 * itemLimit)
                || page < lhsStartPage,
            }"
            class="relative inline-flex items-center border px-4 py-2 text-sm font-medium"
            @click="changePage(page)"
          >
            {{ page / itemLimit }}
          </a>

          <template v-if="canShowSideSeparator">
            <span
              class="relative inline-flex items-center border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700"
            >
              ...
            </span>
            <a
              v-for="(page, index) in pageLinks"
              :key="`right-${index}`"
              href="#"
              :class="{
                'z-10 border-teal-500 bg-teal-50 text-teal-600':
                  isActivePage(page),
                'border-gray-300 bg-white text-gray-500 hover:bg-gray-50':
                  !isActivePage(page),
                'hidden': page > rhsStartPage + 2 * limit || page < rhsStartPage,
              }"
              class="relative inline-flex items-center border px-4 py-2 text-sm font-medium"
              @click="changePage(page)"
            >
              {{ page / itemLimit }}
            </a>
            <a
              href="#"
              class="relative inline-flex items-center rounded-r-md border border-gray-300 bg-white px-2 py-2 text-sm font-medium text-gray-500 hover:bg-gray-50"
              @click="shiftRight"
            >
              <span class="sr-only">Next</span>
              <ChevronRightSmall aria-hidden="true" />
            </a>
          </template>
        </nav>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'GeneralPaginator',

  props: {
    total: {
      type: Number,
      required: true,
    },
    limit: {
      type: Number,
      required: true,
    },
    showPermissionsPagination: {
      type: Boolean,
      default: false,
    },
  },

  emits: ['pageChanged'],

  data() {
    return {
      totalItems: this.total,
      itemLimit: this.limit,
      showing: this.total ? 1 : 0,
      activePage: this.limit,
      lhsStartPage: this.limit,
    }
  },

  computed: {
    rhsStartPage() {
      const lastPage
        = this.totalItems % this.itemLimit === 0
          ? this.totalItems
          : this.totalItems
          - (this.totalItems % this.itemLimit)
          + this.itemLimit
      const expectedRhsStartPage = lastPage - 2 * this.itemLimit
      return this.totalItems > 6 * this.itemLimit ? expectedRhsStartPage : 0
    },
    sideLimit() {
      return 3 * this.itemLimit
    },
    pageLinks() {
      const links = Array(this.totalItems)
        .fill(0)
        .map((_, index) => index + 1)
        .filter(item => item % this.itemLimit === 0)

      if (this.totalItems % this.itemLimit) {
        const finalLinkItem = links[links.length - 1] + this.itemLimit
        links.push(isNaN(finalLinkItem) ? this.itemLimit : finalLinkItem)
      }
      return links
    },
    itemTo() {
      if (this.activePage === this.itemLimit) {
        return this.totalItems > this.itemLimit
          ? this.itemLimit
          : this.totalItems
      }
      return this.totalItems > this.activePage
        ? this.activePage
        : this.totalItems
    },
    canShowSideSeparator() {
      return this.rhsStartPage - this.lhsStartPage >= 6 * this.itemLimit
    },
  },

  watch: {
    total(newTotal) {
      this.totalItems = newTotal
      if (this.activePage - this.totalItems >= this.itemLimit) {
        this.changePage(
          this.activePage - this.itemLimit > this.totalItems
            ? this.itemLimit // Just end looping check and begin from the start
            : this.activePage - this.itemLimit,
        )
      }
    },
    limit(newLimit) {
      this.itemLimit = newLimit
    },
  },

  methods: {
    nextPage() {
      if (this.activePage < this.pageLinks[this.pageLinks.length - 1]) {
        this.activePage += this.itemLimit
        this.$emit('pageChanged', this.activePage)
      }
    },
    prevPage() {
      if (this.activePage > this.itemLimit) {
        this.activePage -= this.itemLimit
        this.$emit('pageChanged', this.activePage)
      }
    },
    isActivePage(page) {
      return page === this.activePage
    },
    changePage(page) {
      if (page === this.activePage) {
        return
      }
      this.activePage = page
      this.showing = page - this.itemLimit + 1
      this.$emit('pageChanged', page)
    },
    shiftRight() {
      this.lhsStartPage = this.lhsStartPage + this.sideLimit
      this.changePage(this.lhsStartPage)
    },
    shiftLeft() {
      this.lhsStartPage
        = this.lhsStartPage - this.sideLimit < this.itemLimit
          ? this.itemLimit
          : this.lhsStartPage - this.sideLimit
      this.changePage(this.lhsStartPage)
    },
  },
}
</script>
